import React from "react";
import { Alert, Box, Typography, Divider, Stack, Paper } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { ExpenseResponseDto } from "../../shared/dtos/expense/ExpenseResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";

const ViewExpenseModal: React.FC<{
  open: boolean;
  onClose: () => void;
  expense: ExpenseResponseDto | null;
  loading: boolean;
  error: string | null;
}> = ({ open, onClose, expense, loading, error }) => {
  const renderContent = (): React.ReactNode => {
    if (loading)
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <AppCircularProgress />
        </Box>
      );
    if (error)
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    if (!expense)
      return (
        <Typography sx={{ align: "center", py: 3 }}>
          No data available.
        </Typography>
      );

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <DetailItem label="Expense Category" value={expense.categoryName} />
          <DetailItem label="Total Amount">
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", color: "error.main" }}
            >
              {formatPlainNumber(expense.amount)}
            </Typography>
          </DetailItem>
          <DetailItem label="Processed By" value={expense.processedBy} />
          <DetailItem
            label="Date & Time"
            value={
              expense.createdAt
                ? new Date(expense.createdAt).toLocaleString()
                : "N/A"
            }
          />
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Description / Reason
          </Typography>
        </Divider>
        <Paper
          variant="outlined"
          sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}
        >
          <Typography variant="body2">
            {expense.description || "No description provided."}
          </Typography>
        </Paper>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Funding Sources
          </Typography>
        </Divider>
        <Stack spacing={1}>
          {expense.payments && expense.payments.length > 0 ? (
            expense.payments.map((p) => (
              <Box
                key={p.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 1.5,
                  bgcolor: "background.default",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {p.paymentMethodName}
                  </Typography>
                  {p.referenceCode && (
                    <Typography variant="caption" color="text.secondary">
                      Ref: {p.referenceCode}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "info.main", fontWeight: "bold" }}
                >
                  {formatPlainNumber(p.amount)}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.disabled" align="center">
              No payment details available.
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Expense Record Details"
      width="sm"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewExpenseModal;
